<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import AgingDonutChart from '@/components/data-visualization/charts/AgingDonutChart.vue'
import { useCommercialDashboardStore } from '@/stores/useCommercialDashboardStore'
import { formatAmount } from './dashboardFormat'

const store = useCommercialDashboardStore()
const { scopedKpis, scopedStats } = storeToRefs(store)

function formatPct(value: number | null): string {
  return value == null ? '—' : `${Math.round(value)} %`
}

/** Tranches de la balance âgée pour le donut (les tranches à 0 sont masquées). */
const agingSlices = computed(() => {
  const a = scopedStats.value.aging
  return [
    { name: 'Non échu', value: a.current, color: '#34d399' },
    { name: '1-30 j', value: a.d1_30, color: '#facc15' },
    { name: '31-60 j', value: a.d31_60, color: '#fb923c' },
    { name: '61-90 j', value: a.d61_90, color: '#f87171' },
    { name: '+90 j', value: a.d90plus, color: '#b91c1c' },
  ].filter((s) => s.value > 0)
})
</script>

<template>
  <!-- Indicateurs + balance âgée côte à côte (≥ lg) -->
  <div class="flex flex-col gap-2.5 lg:flex-row lg:items-start">
    <!-- Indicateurs (compacts, recalculés selon l'onglet actif) -->
    <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:flex-1 lg:order-2">
      <!-- CA facturé -->
      <div class="card bg-base-100 shadow-sm border-l-4 border-emerald-400">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            CA facturé (HT)
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Total facturé hors taxes (champ total_invoiced Odoo, net des avoirs) cumulé sur les clients affichés.">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight text-emerald-600">{{ formatAmount(scopedKpis.totalInvoiced) }}</p>
        </div>
      </div>
      <!-- Impayés -->
      <div class="card bg-base-100 shadow-sm border-l-4" :class="scopedKpis.totalOutstanding > 0 ? 'border-error' : 'border-base-300'">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            Impayés (TTC)
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Montant restant dû, toutes taxes comprises, sur les factures non réglées. Cliquez un montant dans le tableau pour le détail.">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight" :class="scopedKpis.totalOutstanding > 0 ? 'text-error' : ''">
            {{ formatAmount(scopedKpis.totalOutstanding) }}
          </p>
        </div>
      </div>
      <!-- Avoirs -->
      <div class="card bg-base-100 shadow-sm border-l-4" :class="scopedKpis.totalCreditNotes > 0 ? 'border-blue-400' : 'border-base-300'">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            Avoirs (TTC)
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Montant disponible, toutes taxes comprises, sur les avoirs (notes de crédit) émis et non encore utilisés. Cliquez un montant dans le tableau pour le détail.">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight" :class="scopedKpis.totalCreditNotes > 0 ? 'text-blue-600' : ''">
            {{ formatAmount(scopedKpis.totalCreditNotes) }}
          </p>
        </div>
      </div>
      <!-- Marge moyenne -->
      <div class="card bg-base-100 shadow-sm border-l-4 border-base-300">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            Marge moyenne
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Marge moyenne pondérée, calculée sur les devis signés (Σ marge / Σ CA signé).">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight">{{ formatPct(scopedStats.marginPct) }}</p>
        </div>
      </div>
      <!-- CA signé 12 mois -->
      <div class="card bg-base-100 shadow-sm border-l-4 border-base-300">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            CA signé · 12 mois
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Montant total des devis signés au cours des 12 derniers mois.">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight">{{ formatAmount(scopedStats.signedCaPeriod) }}</p>
        </div>
      </div>
      <!-- Transformation -->
      <div class="card bg-base-100 shadow-sm border-l-4 border-base-300">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            Transformation
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Taux de transformation : nombre de devis signés / nombre total de devis.">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight">
            {{ formatPct(scopedStats.conversionRate) }}<span class="text-xs font-medium opacity-50"> · {{ scopedStats.signedCount }}/{{ scopedStats.quotesTotal }}</span>
          </p>
        </div>
      </div>
      <!-- Délai signature -->
      <div class="card bg-base-100 shadow-sm border-l-4 border-base-300">
        <div class="card-body p-3 gap-0.5">
          <p class="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            Délai signature
            <span class="tooltip tooltip-bottom cursor-help align-middle text-xs font-normal normal-case opacity-70" data-tip="Délai moyen, en jours, entre la création d'un devis et sa signature.">ⓘ</span>
          </p>
          <p class="text-xl font-bold leading-tight">{{ scopedStats.avgSignDelay == null ? '—' : scopedStats.avgSignDelay + ' j' }}</p>
        </div>
      </div>
    </div>

    <!-- Balance âgée des impayés (camembert), compacte, à gauche des indicateurs (≥ lg) -->
    <div v-if="scopedStats.agingTotal > 0" class="card bg-base-100 shadow-sm shrink-0 lg:order-1 lg:w-80">
      <div class="card-body p-3 gap-1">
        <div class="flex items-center justify-between">
          <h2 class="text-sm font-semibold">Impayés par ancienneté</h2>
          <span class="text-sm font-bold text-error">{{ formatAmount(scopedStats.agingTotal) }}</span>
        </div>
        <AgingDonutChart :data="agingSlices" height-class="h-44" />
      </div>
    </div>

    <!-- Aucun impayé : badge de succès animé à la place du graphique -->
    <div v-else class="card bg-base-100 shadow-sm shrink-0 lg:order-1 lg:w-80">
      <div class="card-body items-center justify-center gap-1 p-3">
        <div class="relative flex h-28 w-28 items-center justify-center">
          <svg class="absolute inset-0 h-full w-full" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="44" fill="none" stroke="#d1fae5" stroke-width="6" />
            <circle
              class="origin-center animate-success-spin"
              cx="50" cy="50" r="44" fill="none" stroke="#34d399" stroke-width="6"
              stroke-linecap="round" stroke-dasharray="180 96"
            />
          </svg>
          <svg class="h-11 w-11 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p class="mt-1 text-sm font-semibold">Aucun impayé</p>
        <p class="text-xs opacity-50">Tous vos clients sont à jour de paiement</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Tooltips daisyUI : fond plein (le thème laissait un fond transparent). */
.tooltip {
  --tooltip-color: #1f2937; /* slate-800 */
  --tooltip-text-color: #ffffff;
}
.tooltip::before {
  background-color: #1f2937;
  color: #ffffff;
}

@keyframes success-spin {
  to {
    transform: rotate(360deg);
  }
}
.animate-success-spin {
  animation: success-spin 3s linear infinite;
}
</style>
